import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, FolderPlus, Gavel, Clock, AlertTriangle, Save, FileCheck2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import TableroPlazos from "@/components/pld/TableroPlazos";
import { api } from "@/lib/api";
import { mxn } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// PLD Bloque 2 · Pieza A — Bandeja del Oficial de Cumplimiento (43ª-II / 30ª / 39ª).
// Flujo: alerta → caso → ficha de análisis prellenada → dictamen estructurado del OC.
const MONITOR_ROLES = ["admin_entidad", "oficial_cumplimiento", "analista_pld"];

function PlazoBadge({ estado, tipo }) {
  if (!estado || estado.dias_restantes === null) return <span className="text-xs text-slate-400">—</span>;
  const d = estado.dias_restantes;
  const cls = estado.vencido ? "bg-red-100 text-red-700" : estado.por_vencer ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  const txt = estado.vencido ? "Vencido" : `${d} día(s)`;
  return <Badge className={`gap-1 ${cls}`} data-testid="plazo-badge"><Clock className="h-3 w-3" /> {txt}</Badge>;
}

function Kpi({ label, value, tone = "text-primary" }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <p className={`mt-1 font-heading text-2xl font-semibold ${tone}`}>{value}</p>
    </div>
  );
}

export default function BandejaOC() {
  const { user } = useAuth();
  const canOpen = MONITOR_ROLES.includes(user?.role);
  const canDictar = user?.role === "oficial_cumplimiento";
  const canEditOC = ["admin_entidad", "oficial_cumplimiento"].includes(user?.role);

  const [data, setData] = useState({ alertas_pendientes: [], casos_en_dictamen: [], casos_listos_para_reporte: [], resumen: {} });
  const [oc, setOc] = useState({ oficial_cumplimiento: {}, comite_control: { integrantes: [] } });
  const [busy, setBusy] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [fichaOpen, setFichaOpen] = useState(false);
  const [dict, setDict] = useState({ resultado: "", tipo_reporte: "Inusual", fundamento: "", gestiones: "" });
  // Tarea 5 (jul-2026): personas relacionadas del caso para el layout RIPS cols 29-34.
  // Se editan aquí, en el diálogo del dictamen, y se persisten en `casos.personas_relacionadas`.
  const [personasRel, setPersonasRel] = useState([]);
  const [savingRel, setSavingRel] = useState(false);
  const [ocOpen, setOcOpen] = useState(false);
  const [ocForm, setOcForm] = useState({});

  const load = useCallback(async () => {
    try {
      const [b, o] = await Promise.all([
        api.get("/pld/oc/bandeja").then((r) => r.data),
        api.get("/pld/oficial").then((r) => r.data),
      ]);
      setData(b); setOc(o); setOcForm({ ...o.oficial_cumplimiento,
        comite_integrantes: (o.comite_control?.integrantes || []).join(", "),
        // Tarea 1 (jul-2026): declaración explícita del tipo de comité de la entidad.
        comite_tipo: o.comite_control?.tipo || "oc_asume_funciones" });
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const abrirCaso = async (alertaId) => {
    setBusy(true);
    try {
      await api.post(`/pld/casos/desde-alerta/${alertaId}`);
      toast.success("Caso abierto para análisis del Oficial de Cumplimiento");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo abrir el caso"); }
    finally { setBusy(false); }
  };

  const abrirFicha = async (casoId) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/pld/casos/${casoId}/ficha`);
      setFicha(data);
      const dx = data.caso?.dictamen || {};
      setDict({ resultado: dx.resultado || "", tipo_reporte: dx.tipo_reporte || "Inusual", fundamento: dx.fundamento || "", gestiones: dx.gestiones || "" });
      // Cargar personas relacionadas del caso (jul-2026, Tarea 5).
      setPersonasRel(Array.isArray(data.caso?.personas_relacionadas) ? data.caso.personas_relacionadas : []);
      setFichaOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo abrir la ficha"); }
    finally { setBusy(false); }
  };

  const dictaminar = async () => {
    if (!dict.resultado) { toast.error("Elige SE REPORTA o NO SE REPORTA"); return; }
    setBusy(true);
    try {
      await api.post(`/pld/casos/${ficha.caso.id}/dictaminar`, dict);
      toast.success(dict.resultado === "SE REPORTA" ? "Dictamen emitido. Caso listo para generar reporte." : "Dictamen emitido. Caso cerrado (no se reporta).");
      setFichaOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo emitir el dictamen"); }
    finally { setBusy(false); }
  };

  // Tarea 5 (jul-2026): guardar la lista completa de personas relacionadas del caso.
  const guardarPersonasRel = async () => {
    if (!ficha?.caso?.id) return;
    setSavingRel(true);
    try {
      const { data } = await api.put(`/pld/casos/${ficha.caso.id}/personas-relacionadas`,
        { personas: personasRel });
      setPersonasRel(data.personas_relacionadas || []);
      toast.success(`Personas relacionadas guardadas (${data.total}).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron guardar las personas relacionadas"); }
    finally { setSavingRel(false); }
  };
  const agregarPersonaRel = () => setPersonasRel((p) => [...p,
    { tipo_persona: "Persona física nacional", nombre: "", apellido_paterno: "", apellido_materno: "",
      rfc: "", curp: "", tipo_relacion: "" }]);
  const editarPersonaRel = (idx, k, v) => setPersonasRel((p) =>
    p.map((row, i) => (i === idx ? { ...row, [k]: v } : row)));
  const eliminarPersonaRel = (idx) => setPersonasRel((p) => p.filter((_, i) => i !== idx));

  const guardarOC = async () => {
    // Feb-2026 · fix P1 iter-101 (Tarea 4): validación cruzada previa. El correo del OC
    // designado formalmente DEBE pertenecer a un usuario existente de la entidad con
    // rol=oficial_cumplimiento. Sin este check, la designación podía apuntar a un correo
    // arbitrario sin usuario real detrás, y el actor que dictamina quedaba desacoplado.
    const correo = String(ocForm.correo || "").trim().toLowerCase();
    if (!correo) {
      toast.error("Captura el correo del Oficial de Cumplimiento designado.");
      return;
    }
    setBusy(true);
    try {
      const { data: usuarios } = await api.get("/usuarios");
      const activos = (usuarios || []).filter((u) => u.activo !== false);
      const matchOC = activos.find((u) => String(u.email || "").toLowerCase() === correo
                                          && u.role === "oficial_cumplimiento");
      if (!matchOC) {
        const existe = activos.find((u) => String(u.email || "").toLowerCase() === correo);
        if (existe) {
          toast.error(`El correo ${correo} existe pero su rol es "${existe.role}", no "Oficial de Cumplimiento". Cambia su rol en Usuarios y roles o designa a un usuario con el rol correcto.`);
        } else {
          toast.error(`No existe un usuario con correo ${correo} y rol Oficial de Cumplimiento en esta entidad. Créalo primero en Usuarios y roles y luego regresa a designarlo aquí.`);
        }
        setBusy(false);
        return;
      }
      const { data } = await api.put("/pld/oficial", {
        ...ocForm, comite_integrantes: (ocForm.comite_integrantes || "").split(",").map((s) => s.trim()).filter(Boolean),
      });
      setOc(data); setOcOpen(false);
      toast.success(`Datos del Oficial de Cumplimiento actualizados (vinculado a usuario ${matchOC.name}).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setBusy(false); }
  };

  const ocd = oc.oficial_cumplimiento || {};

  return (
    <div className="space-y-5" data-testid="bandeja-oc">
      {/* PLD Bloque 2 · Pieza C — tablero de plazos con semáforo (28ª/29ª/30ª/33ª/43ª-II) */}
      <TableroPlazos />

      {/* Figura del OC (39ª) */}
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="oc-figura-card">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-6 w-6 text-accent" />
          <div className="text-sm">
            <p className="font-heading font-semibold text-slate-800">{ocd.nombre || "Oficial de Cumplimiento sin designar"}</p>
            <p className="text-slate-500">
              {ocd.rfc ? `RFC ${ocd.rfc} · ` : ""}{ocd.oficio_cnbv ? `Oficio CNBV ${ocd.oficio_cnbv}` : "Falta oficio de designación (39ª)"}
              {ocd.fecha_designacion ? ` · designado ${ocd.fecha_designacion}` : ""}
            </p>
            <p className="text-xs text-slate-400" data-testid="oc-comite-label">
              {oc.comite_control?.tipo === "comite_constituido"
                ? `Comité de Comunicación y Control constituido${(oc.comite_control?.integrantes || []).length ? ": " + (oc.comite_control?.integrantes || []).join(", ") : ""}`
                : "El Oficial de Cumplimiento asume las funciones del Comité"}
            </p>
          </div>
        </div>
        {canEditOC && (
          <Button data-testid="oc-editar-btn" size="sm" variant="outline" onClick={() => setOcOpen(true)}>
            <Pencil className="mr-1.5 h-4 w-4" /> Datos del OC
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Alertas pendientes" value={data.resumen?.pendientes ?? 0} />
        <Kpi label="Casos en dictamen" value={data.resumen?.en_dictamen ?? 0} />
        <Kpi label="Por vencer (≤7 días)" value={data.resumen?.por_vencer ?? 0} tone="text-amber-600" />
        <Kpi label="Listos para reporte" value={data.resumen?.listos ?? 0} tone="text-accent" />
      </div>

      {/* Alertas pendientes de análisis */}
      <div>
        <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><AlertTriangle className="h-4 w-4 text-amber-600" /> Alertas pendientes de análisis</h4>
        <DataTable testid="table-alertas-pendientes" rows={data.alertas_pendientes}
          columns={[
            { key: "folio", label: "Folio" },
            { key: "cliente", label: "Cliente" },
            { key: "regla", label: "Regla" },
            { key: "nivel_riesgo", label: "Nivel", kind: "risk" },
          ]}
          actions={canOpen ? (row) => (
            <Button data-testid={`abrir-caso-${row.id}`} size="sm" variant="outline" disabled={busy} onClick={() => abrirCaso(row.id)}>
              <FolderPlus className="mr-1.5 h-4 w-4" /> Abrir caso
            </Button>
          ) : undefined}
          emptyLabel="Sin alertas pendientes de análisis." />
      </div>

      {/* Casos en dictamen */}
      <div>
        <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
          <Gavel className="h-4 w-4 text-primary" /> Casos en dictamen
          {data.resumen?.reporte_24h > 0 && (
            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700"
                  data-testid="badge-conteo-24h">
              {data.resumen.reporte_24h} en 24h
            </span>
          )}
        </h4>
        <DataTable testid="table-casos-dictamen" rows={data.casos_en_dictamen}
          columns={[
            { key: "folio", label: "Folio",
              render: (r) => (
                <div className="flex items-center gap-2" data-testid={`caso-row-${r.id}`}>
                  {r.reporte_24h && (
                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-700"
                          data-testid={`badge-24h-${r.id}`}>24H</span>
                  )}
                  <span>{r.folio}</span>
                </div>
              ) },
            { key: "cliente", label: "Cliente" },
            { key: "regla", label: "Origen" },
            { key: "plazo_tipo", label: "Plazo" },
            { key: "plazo_estado", label: "Restante", render: (r) => <PlazoBadge estado={r.plazo_estado} tipo={r.plazo_tipo} /> },
          ]}
          actions={(row) => (
            <Button data-testid={`analizar-caso-${row.id}`} size="sm" variant="outline" disabled={busy} onClick={() => abrirFicha(row.id)}>
              <Gavel className="mr-1.5 h-4 w-4" /> Analizar
            </Button>
          )}
          emptyLabel="Sin casos en dictamen." />
      </div>

      {/* Casos listos para reporte */}
      {data.casos_listos_para_reporte?.length > 0 && (
        <div>
          <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><FileCheck2 className="h-4 w-4 text-accent" /> Listos para generar reporte (Bloque 2 · Pieza B)</h4>
          <DataTable testid="table-casos-listos" rows={data.casos_listos_para_reporte}
            columns={[
              { key: "folio", label: "Folio" },
              { key: "cliente", label: "Cliente" },
              { key: "regla", label: "Origen" },
              { key: "dictamen", label: "Tipo de reporte", render: (r) => r.dictamen?.tipo_reporte || "—" },
            ]}
            emptyLabel="—" />
        </div>
      )}

      {/* Diálogo: ficha de análisis + dictamen */}
      <Dialog open={fichaOpen} onOpenChange={setFichaOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="ficha-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ficha de análisis — {ficha?.caso?.folio}</DialogTitle>
            <DialogDescription>Operación inusual (30ª). Datos prellenados; el OC dictamina (39ª).</DialogDescription>
          </DialogHeader>
          {ficha && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3">
                <div><span className="text-slate-500">Cliente:</span> <strong>{ficha.cliente?.nombre || ficha.cliente?.razon_social}</strong></div>
                <div><span className="text-slate-500">RFC:</span> {ficha.cliente?.rfc || "—"}</div>
                <div><span className="text-slate-500">Actividad:</span> {ficha.cliente?.actividad_economica || ficha.cliente?.giro_mercantil || "—"}</div>
                <div><span className="text-slate-500">Riesgo:</span> <StatusBadge value={ficha.cliente?.nivel_riesgo} /></div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="font-semibold text-amber-800">Operación / patrón que disparó</p>
                <p className="text-amber-700">{ficha.operacion_disparadora?.detalle}</p>
              </div>
              <div className="grid grid-cols-3 gap-3 rounded-lg border border-slate-200 p-3">
                <div><p className="text-xs text-slate-500">Perfil declarado</p><p className="font-mono">{ficha.perfil?.estimado ? mxn(ficha.perfil.estimado) : "—"}</p></div>
                <div><p className="text-xs text-slate-500">Real máx. mensual</p><p className="font-mono">{ficha.perfil?.calculado?.monto_mensual_max ? mxn(ficha.perfil.calculado.monto_mensual_max) : "—"}</p></div>
                <div><p className="text-xs text-slate-500">Op. relevantes</p><p className="font-mono">{ficha.operaciones_relevantes?.length || 0}</p></div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <span className="text-slate-500">Antecedentes: {ficha.antecedentes?.casos_previos} caso(s), {ficha.antecedentes?.alertas_totales} alerta(s)</span>
                <span><PlazoBadge estado={ficha.plazo} tipo={ficha.plazo?.plazo_tipo} /> <span className="ml-1 text-xs text-slate-400">{ficha.plazo?.plazo_tipo}</span></span>
              </div>

              {/* Tarea 3 (jul-2026): banner destacado en la ficha cuando el caso es 24h. Se
                  muestra ANTES del dictamen para que el OC sepa el plazo desde el primer scroll. */}
              {ficha.caso?.reporte_24h && (
                <div className="flex items-center gap-2 rounded-lg border-2 border-red-400 bg-red-50 p-3 text-sm text-red-900"
                     data-testid="banner-reporte-24h">
                  <span className="font-semibold uppercase tracking-wide">Reporte de 24 horas</span>
                  <span>·</span>
                  <span>Este caso proviene de coincidencia LPB o indicios de FT. El archivo
                    llevará la leyenda “REPORTE DE 24 HORAS.” en la columna 35 y debe
                    dictaminarse dentro del plazo restante.
                  </span>
                </div>
              )}

              {/* Dictamen */}
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="dictamen-form">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Dictamen del Oficial de Cumplimiento (39ª)</p>
                {ficha.caso?.dictamen?.resultado && (
                  <div className="text-xs text-slate-500 space-y-0.5" data-testid="dictamen-previo">
                    <p>Dictamen previo: <strong>{ficha.caso.dictamen.resultado}</strong> por {ficha.caso.dictamen.dictaminado_por} el {(ficha.caso.dictamen.fecha_determinacion || "").slice(0, 10)}</p>
                    {/* Tarea 2 (jul-2026): mostrar el carácter con el que se firmó el dictamen. */}
                    {ficha.caso.dictamen.caracter && (
                      <p data-testid="dictamen-caracter">
                        <span className="text-slate-500">Carácter:</span>{" "}
                        <strong className="text-slate-700">{ficha.caso.dictamen.caracter}</strong>
                      </p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1 block text-xs">Resultado</Label>
                    <Select value={dict.resultado} onValueChange={(v) => setDict((p) => ({ ...p, resultado: v }))} disabled={!canDictar}>
                      <SelectTrigger data-testid="dictamen-resultado"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SE REPORTA">SE REPORTA</SelectItem>
                        <SelectItem value="NO SE REPORTA">NO SE REPORTA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {dict.resultado === "SE REPORTA" && (
                    <div>
                      <Label className="mb-1 block text-xs">Tipo de reporte</Label>
                      <Select value={dict.tipo_reporte} onValueChange={(v) => setDict((p) => ({ ...p, tipo_reporte: v }))} disabled={!canDictar}>
                        <SelectTrigger data-testid="dictamen-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Inusual">Operación Inusual</SelectItem>
                          <SelectItem value="Interna Preocupante">Interna Preocupante</SelectItem>
                          <SelectItem value="Coincidencia LPB">Coincidencia LPB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Fundamento / motivación</Label>
                  <Textarea data-testid="dictamen-fundamento" rows={3} value={dict.fundamento} disabled={!canDictar}
                    onChange={(e) => setDict((p) => ({ ...p, fundamento: e.target.value }))}
                    placeholder="Razonamiento del OC: por qué se reporta o no, con base en el perfil y la operación." />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Gestiones realizadas</Label>
                  <Textarea data-testid="dictamen-gestiones" rows={2} value={dict.gestiones} disabled={!canDictar}
                    onChange={(e) => setDict((p) => ({ ...p, gestiones: e.target.value }))} />
                </div>

                {/* Tarea 5 (jul-2026): personas relacionadas del caso para el layout RIPS
                    cols 29-34. Cada persona genera una fila adicional al reporte. */}
                {dict.resultado === "SE REPORTA" && (
                  <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="personas-relacionadas-panel">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                        Personas relacionadas ({personasRel.length})
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={agregarPersonaRel} disabled={!canEditOC}
                                data-testid="agregar-persona-rel-btn">
                          <FolderPlus className="mr-1 h-3.5 w-3.5" /> Agregar
                        </Button>
                        <Button size="sm" onClick={guardarPersonasRel}
                                disabled={savingRel || !canEditOC}
                                data-testid="guardar-personas-rel-btn"
                                className="bg-primary hover:bg-sidebar-hover">
                          {savingRel ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
                          Guardar
                        </Button>
                      </div>
                    </div>
                    <p className="mb-2 text-[11px] text-slate-500">
                      Los datos de estas personas se emiten en las <strong>columnas 29-34</strong> del layout
                      RIPS del reporte Inusual/Interna Preocupante. Cada persona ocupa una fila adicional
                      con consecutivo 01, 02, … Reglas: al menos nombre o razón social + RFC/CURP.
                    </p>
                    {personasRel.length === 0 ? (
                      <p className="text-xs text-slate-400" data-testid="personas-rel-vacio">
                        Sin personas relacionadas. Agrega si el caso involucra apoderados, cotitulares,
                        beneficiarios o proveedores de fondos.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {personasRel.map((p, idx) => (
                          <div key={idx} className="rounded border border-slate-200 bg-slate-50/60 p-2"
                               data-testid={`persona-rel-row-${idx}`}>
                            <div className="mb-1 flex items-center justify-between">
                              <span className="text-xs font-medium text-slate-600">#{idx + 1}</span>
                              <Button size="sm" variant="ghost" className="h-6 text-red-500"
                                      onClick={() => eliminarPersonaRel(idx)} disabled={!canEditOC}
                                      data-testid={`eliminar-persona-rel-${idx}`}>Quitar</Button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <Label className="mb-0.5 block text-[10px] text-slate-500">Tipo de persona</Label>
                                <Select value={p.tipo_persona || "Persona física nacional"}
                                        onValueChange={(v) => editarPersonaRel(idx, "tipo_persona", v)}
                                        disabled={!canEditOC}>
                                  <SelectTrigger className="h-8 text-xs" data-testid={`persona-rel-tp-${idx}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Persona física nacional">Persona física</SelectItem>
                                    <SelectItem value="Persona moral">Persona moral</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="mb-0.5 block text-[10px] text-slate-500">Tipo de relación</Label>
                                <Input className="h-8 text-xs" value={p.tipo_relacion || ""} disabled={!canEditOC}
                                       onChange={(e) => editarPersonaRel(idx, "tipo_relacion", e.target.value)}
                                       placeholder="APODERADO, SOCIO, BENEFICIARIO…"
                                       data-testid={`persona-rel-tipo-${idx}`} />
                              </div>
                              <div>
                                <Label className="mb-0.5 block text-[10px] text-slate-500">RFC</Label>
                                <Input className="h-8 text-xs uppercase" value={p.rfc || ""} disabled={!canEditOC}
                                       onChange={(e) => editarPersonaRel(idx, "rfc", e.target.value)}
                                       maxLength={13}
                                       data-testid={`persona-rel-rfc-${idx}`} />
                              </div>
                              {p.tipo_persona !== "Persona moral" ? (
                                <>
                                  <div>
                                    <Label className="mb-0.5 block text-[10px] text-slate-500">Nombre(s)</Label>
                                    <Input className="h-8 text-xs" value={p.nombre || ""} disabled={!canEditOC}
                                           onChange={(e) => editarPersonaRel(idx, "nombre", e.target.value)}
                                           data-testid={`persona-rel-nombre-${idx}`} />
                                  </div>
                                  <div>
                                    <Label className="mb-0.5 block text-[10px] text-slate-500">Apellido paterno</Label>
                                    <Input className="h-8 text-xs" value={p.apellido_paterno || ""} disabled={!canEditOC}
                                           onChange={(e) => editarPersonaRel(idx, "apellido_paterno", e.target.value)}
                                           data-testid={`persona-rel-appat-${idx}`} />
                                  </div>
                                  <div>
                                    <Label className="mb-0.5 block text-[10px] text-slate-500">Apellido materno</Label>
                                    <Input className="h-8 text-xs" value={p.apellido_materno || ""} disabled={!canEditOC}
                                           onChange={(e) => editarPersonaRel(idx, "apellido_materno", e.target.value)}
                                           data-testid={`persona-rel-apmat-${idx}`} />
                                  </div>
                                  <div className="col-span-3">
                                    <Label className="mb-0.5 block text-[10px] text-slate-500">CURP</Label>
                                    <Input className="h-8 text-xs uppercase" value={p.curp || ""} disabled={!canEditOC}
                                           onChange={(e) => editarPersonaRel(idx, "curp", e.target.value)}
                                           maxLength={18}
                                           data-testid={`persona-rel-curp-${idx}`} />
                                  </div>
                                </>
                              ) : (
                                <div className="col-span-3">
                                  <Label className="mb-0.5 block text-[10px] text-slate-500">Razón social</Label>
                                  <Input className="h-8 text-xs" value={p.razon_social || ""} disabled={!canEditOC}
                                         onChange={(e) => editarPersonaRel(idx, "razon_social", e.target.value)}
                                         data-testid={`persona-rel-razon-${idx}`} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!canDictar && <p className="text-xs text-amber-600">Solo el Oficial de Cumplimiento puede emitir el dictamen (39ª).</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFichaOpen(false)}>Cerrar</Button>
            {canDictar && (
              <Button data-testid="dictaminar-btn" onClick={dictaminar} disabled={busy} className="bg-primary hover:bg-sidebar-hover">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gavel className="mr-2 h-4 w-4" />} Emitir dictamen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: datos formales del OC */}
      <Dialog open={ocOpen} onOpenChange={setOcOpen}>
        <DialogContent data-testid="oc-form">
          <DialogHeader>
            <DialogTitle className="font-heading">Oficial de Cumplimiento (designación · 39ª)</DialogTitle>
            <DialogDescription className="sr-only">Datos formales del OC</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="mb-1 block text-xs">Nombre</Label><Input data-testid="oc-nombre" value={ocForm.nombre || ""} onChange={(e) => setOcForm((p) => ({ ...p, nombre: e.target.value }))} /></div>
            <div><Label className="mb-1 block text-xs">RFC</Label><Input data-testid="oc-rfc" value={ocForm.rfc || ""} onChange={(e) => setOcForm((p) => ({ ...p, rfc: e.target.value }))} /></div>
            <div><Label className="mb-1 block text-xs">Fecha de designación</Label><Input data-testid="oc-fecha" type="date" value={ocForm.fecha_designacion || ""} onChange={(e) => setOcForm((p) => ({ ...p, fecha_designacion: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="mb-1 block text-xs">Oficio / folio CNBV de designación</Label><Input data-testid="oc-oficio" value={ocForm.oficio_cnbv || ""} onChange={(e) => setOcForm((p) => ({ ...p, oficio_cnbv: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="mb-1 block text-xs">Correo</Label><Input data-testid="oc-correo" value={ocForm.correo || ""} onChange={(e) => setOcForm((p) => ({ ...p, correo: e.target.value }))} placeholder="correo@dominio.com — debe existir como usuario con rol Oficial de Cumplimiento" /></div>
            {/* Tarea 1 (jul-2026): declaración obligatoria del tipo de comité. Dos opciones únicas. */}
            <div className="col-span-2" data-testid="oc-comite-tipo-wrapper">
              <Label className="mb-1 block text-xs">
                ¿La entidad cuenta con Comité de Comunicación y Control?
              </Label>
              <Select value={ocForm.comite_tipo || "oc_asume_funciones"}
                       onValueChange={(v) => setOcForm((p) => ({ ...p, comite_tipo: v }))}>
                <SelectTrigger data-testid="oc-comite-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="comite_constituido" data-testid="oc-comite-tipo-constituido">
                    Sí, el Comité está constituido formalmente
                  </SelectItem>
                  <SelectItem value="oc_asume_funciones" data-testid="oc-comite-tipo-oc">
                    No, el Oficial de Cumplimiento asume sus funciones
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-slate-500">
                Aplica a los dictámenes que se emitan a partir de este cambio. Los dictámenes
                previos conservan el carácter con el que se firmaron.
              </p>
            </div>
            <div className="col-span-2"><Label className="mb-1 block text-xs">Integrantes del Comité (separados por coma)</Label><Input data-testid="oc-comite" value={ocForm.comite_integrantes || ""} onChange={(e) => setOcForm((p) => ({ ...p, comite_integrantes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOcOpen(false)}>Cancelar</Button>
            <Button data-testid="oc-guardar-btn" onClick={guardarOC} disabled={busy} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
